import { useCallback, useEffect, useState } from 'react';
import { Button, useToast } from '@invested/ui';
import * as api from '../../services/api';
import type { ChannelType, NotificationChannel, NotificationStatus } from '../../types';

const TRIGGERS = ['checkpoint_resolved', 'new_blocker'];

export function NotificationConfig({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [status, setStatus] = useState<NotificationStatus | null>(null);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [channel, setChannel] = useState<ChannelType>('email');
  const [target, setTarget] = useState('');
  const [triggerOn, setTriggerOn] = useState(TRIGGERS[0]);

  const load = useCallback(async () => {
    const [st, chs] = await Promise.all([api.getNotificationStatus(), api.getChannels(projectId)]);
    setStatus(st);
    setChannels(chs);
  }, [projectId]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  async function add() {
    if (!target.trim()) return;
    try {
      // Create disabled if the channel type can't deliver right now (e.g.
      // Telegram stub), so an unavailable channel never silently no-ops.
      await api.createChannel({ projectId, channel, target: target.trim(), triggerOn, enabled: avail(channel) });
      setTarget('');
      await load();
      if (!avail(channel)) toast.warning('Kanalen er lagt til, men er ikke tilgjengelig ennå (opprettet av).');
    } catch {
      toast.danger('Kunne ikke legge til kanal.');
    }
  }

  async function toggle(ch: NotificationChannel) {
    await api.updateChannel(ch.id, { enabled: !ch.enabled });
    await load();
  }

  async function remove(ch: NotificationChannel) {
    await api.deleteChannel(ch.id);
    await load();
  }

  async function test(ch: NotificationChannel) {
    const r = await api.testChannel(ch.id);
    if (r.delivered) toast.success('Testvarsel sendt.');
    else toast.warning(`Ikke sendt: ${r.reason ?? 'ukjent'}`);
    await load();
  }

  const avail = (c: ChannelType) => status?.[c]?.available ?? false;

  function deliveryLabel(ch: NotificationChannel): { text: string; className: string } | null {
    if (!ch.lastDeliveryAt) return null;
    const when = new Date(ch.lastDeliveryAt).toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    if (ch.lastDeliveryOk) return { text: `✓ levert ${when}`, className: 'text-emerald-600' };
    return { text: `✗ ${ch.lastDeliveryReason ?? 'feilet'} (${when})`, className: 'text-red-600' };
  }

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-black/45">Varsling</h3>

      {status && (
        <p className="mb-2 text-[11px] text-black/45">
          Sentral tjeneste{' '}
          {status.serviceReachable === false ? (
            <span className="text-red-600">● utilgjengelig</span>
          ) : (
            <span className="text-emerald-600">● oppe</span>
          )}
          {' — '}
          <span className={avail('email') ? 'text-emerald-600' : 'text-black/40'}>e-post {avail('email') ? 'på' : 'av'}</span>
          {' · '}
          <span className={avail('sms') ? 'text-emerald-600' : 'text-black/40'}>SMS {avail('sms') ? 'på' : 'av'}</span>
          {' · '}
          <span className="text-black/40">Telegram stub</span>
        </p>
      )}

      <ul className="space-y-1">
        {channels.map((ch) => {
          const unavailable = !avail(ch.channel);
          const delivery = deliveryLabel(ch);
          return (
            <li key={ch.id} className="rounded-lg border border-black/[0.07] px-2 py-1.5 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs font-semibold uppercase text-black/50">{ch.channel}</span>
                <span className="min-w-0 flex-1 truncate">{ch.target}</span>
                <span className="shrink-0 font-mono text-[11px] text-black/40">{ch.triggerOn}</span>
                {unavailable && (
                  <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title="Kanalen kan ikke levere akkurat nå">
                    ikke tilgjengelig
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void toggle(ch)}
                  disabled={unavailable && !ch.enabled}
                  title={unavailable && !ch.enabled ? 'Kanalen er ikke tilgjengelig' : ''}
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold disabled:opacity-40 ${
                    ch.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-black/5 text-black/40'
                  }`}
                >
                  {ch.enabled ? 'på' : 'av'}
                </button>
                <button type="button" onClick={() => void test(ch)} className="shrink-0 text-[11px] text-sky-600 hover:underline">
                  test
                </button>
                <button type="button" onClick={() => void remove(ch)} className="shrink-0 text-black/25 hover:text-red-600" aria-label="Slett kanal">
                  ×
                </button>
              </div>
              {delivery && <p className={`mt-0.5 pl-[4.5rem] text-[11px] ${delivery.className}`}>{delivery.text}</p>}
            </li>
          );
        })}
        {channels.length === 0 && <li className="text-xs text-black/40">Ingen varslingskanaler.</li>}
      </ul>

      <div className="mt-2 flex flex-wrap gap-2">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as ChannelType)}
          className="rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm"
        >
          <option value="email">E-post{avail('email') ? '' : ' (av)'}</option>
          <option value="sms">SMS{avail('sms') ? '' : ' (av)'}</option>
          <option value="telegram">Telegram (ikke tilgjengelig)</option>
        </select>
        <input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder={channel === 'email' ? 'mottaker@…' : channel === 'sms' ? '+47…' : 'chat_id'}
          className="flex-1 rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-black/30"
        />
        <select
          value={triggerOn}
          onChange={(e) => setTriggerOn(e.target.value)}
          className="rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm"
        >
          {TRIGGERS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <Button size="sm" variant="secondary" onClick={() => void add()}>
          Legg til
        </Button>
      </div>
    </section>
  );
}
